/// <reference lib="dom" />
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  clampPosition,
  firstFocusable,
  isFocusable,
  resolveCalendarKey,
  type CalendarKey,
  type FocusGrid,
  type FocusPosition,
} from './calendar-keyboard-model';

/**
 * The roving-tabindex controller for the Calendar_View composite widget
 * (task 22.4, R10.11, R10.13, R18.6).
 *
 * This hook is the DOM-and-React half of the keyboard model: it owns the current
 * roving focus position, keeps exactly one bar or marker in the tab order at a
 * time, moves DOM focus in response to the arrow keys, opens the Detail_Panel on
 * Enter/Space, and restores focus to the originating bar when the panel closes.
 * The *decisions* — which item an arrow moves to, whether a key activates — come
 * from the pure {@link resolveCalendarKey}; this hook only carries them out
 * against the live DOM. Keeping the two apart is what lets the navigation
 * contract be unit-tested without a browser while the wiring is exercised by a
 * component test.
 *
 * ## Roving tabindex
 *
 * Every registered item asks the hook for its {@link RovingItem.tabIndex}: the one
 * item at the current roving position gets `0` (it is the widget's single tab
 * stop), every other gets `-1`. Tabbing into the grid therefore lands on the
 * roving item; the arrow keys then move the roving position, which moves both the
 * `0` tabindex and DOM focus. This is the WAI-ARIA composite-widget pattern the
 * task names.
 *
 * ## Focus restore across the Detail_Panel
 *
 * Enter/Space records the activating item as the *restore target* and opens the
 * panel. When the caller signals the panel has closed ({@link RovingFocusApi
 * .notifyDetailClosed}), the hook returns focus to that item — the "focus returns
 * to the originating bar on close" of the task and §9's gap #2. If the item no
 * longer exists (the Initiative left the result set while the panel was open),
 * focus falls back to the current roving item.
 */

/** A registered focusable item's props, applied by the bar/marker component. */
export interface RovingItem {
  /** `0` for the single roving item, `-1` for every other (roving tabindex). */
  readonly tabIndex: 0 | -1;
  /** Ref callback registering the element at this grid position. */
  readonly ref: (element: HTMLElement | null) => void;
  /** `keydown` handler translating the arrow/Enter/Space keys into navigation. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  /** `focus` handler adopting the roving position when the user tabs/clicks in. */
  readonly onFocus: () => void;
}

/** The hook's public surface consumed by {@link import('./CalendarGrid').CalendarGrid}. */
export interface RovingFocusApi {
  /** Props for the item at grid position `(lane, item)`. */
  readonly itemProps: (lane: number, item: number) => RovingItem;
  /** Signals the Detail_Panel closed, so focus returns to the originating item. */
  readonly notifyDetailClosed: () => void;
}

/** The set of keys the composite widget consumes; everything else falls through. */
const HANDLED_KEYS: ReadonlySet<string> = new Set<CalendarKey>([
  'ArrowRight',
  'ArrowLeft',
  'ArrowDown',
  'ArrowUp',
  'Enter',
  ' ',
]);

export interface RovingFocusOptions {
  /** The focus grid: the item count per lane, in lane order. */
  readonly grid: FocusGrid;
  /** Opens the Detail_Panel for the item at `position` (Enter/Space activation). */
  readonly onActivate: (position: FocusPosition) => void;
  /**
   * Brings lane `lane` into view before focus moves to it, for a virtualized grid
   * where the target lane may not be mounted. Optional: when absent, focus moves
   * only among mounted items.
   */
  readonly scrollLaneIntoView?: ((lane: number) => void) | undefined;
}

/** A stable key for a grid position, used to register and look up DOM elements. */
function positionKey(position: FocusPosition): string {
  return `${String(position.lane)}:${String(position.item)}`;
}

export function useRovingCalendarFocus({
  grid,
  onActivate,
  scrollLaneIntoView,
}: RovingFocusOptions): RovingFocusApi {
  // The current roving position. Seeded to the grid's first focusable item and
  // re-clamped whenever the grid changes shape, so it always points at a real
  // item (or is null for an empty grid).
  const [position, setPosition] = useState<FocusPosition | null>(() => firstFocusable(grid));

  // The DOM elements registered by position key, so a move can focus the target.
  const elements = useRef(new Map<string, HTMLElement>());
  // The item that opened the Detail_Panel, to restore focus to on close.
  const restoreTarget = useRef<FocusPosition | null>(null);
  // Whether a move requested DOM focus that has not yet been applied (the target
  // element may mount only after a virtualization scroll), applied in an effect.
  const pendingFocus = useRef<FocusPosition | null>(null);

  // Keep the roving position valid as the grid changes (a lane collapses, a
  // filter narrows the result). Snap a stale position back onto a real item.
  useEffect(() => {
    setPosition((current) => {
      if (current === null) return firstFocusable(grid);
      if (isFocusable(grid, current)) return current;
      return clampPosition(grid, current);
    });
  }, [grid]);

  // Apply a pending DOM focus once the target element is registered. Runs after
  // render so a just-scrolled-into-view lane's element is present.
  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;
    const element = elements.current.get(positionKey(target));
    if (element) {
      element.focus();
      pendingFocus.current = null;
    }
  });

  const focusPosition = useCallback(
    (target: FocusPosition): void => {
      setPosition(target);
      pendingFocus.current = target;
      scrollLaneIntoView?.(target.lane);
      // Focus synchronously when the element is already mounted; otherwise the
      // effect above applies it after the scroll mounts the row.
      const element = elements.current.get(positionKey(target));
      if (element) {
        element.focus();
        pendingFocus.current = null;
      }
    },
    [scrollLaneIntoView],
  );

  const notifyDetailClosed = useCallback((): void => {
    const target = restoreTarget.current;
    restoreTarget.current = null;
    const resolved = target && isFocusable(grid, target) ? target : position;
    if (resolved) focusPosition(resolved);
  }, [grid, position, focusPosition]);

  const itemProps = useCallback(
    (lane: number, item: number): RovingItem => {
      const here: FocusPosition = { lane, item };
      const isRoving = position !== null && position.lane === lane && position.item === item;
      return {
        tabIndex: isRoving ? 0 : -1,
        ref: (element) => {
          const key = positionKey(here);
          if (element) elements.current.set(key, element);
          else elements.current.delete(key);
        },
        onFocus: () => {
          // Adopt the position the browser actually focused (a pointer click or a
          // Tab into the grid), so the roving item and DOM focus never diverge.
          setPosition((current) =>
            current && current.lane === lane && current.item === item ? current : here,
          );
        },
        onKeyDown: (event) => {
          if (!HANDLED_KEYS.has(event.key)) return;
          const result = resolveCalendarKey(grid, here, event.key as CalendarKey);
          if (result.kind === 'none') {
            // Still swallow the arrow keys so a captured arrow does not also scroll
            // the grid container; a no-op move should not leak to the page.
            event.preventDefault();
            return;
          }
          event.preventDefault();
          if (result.kind === 'activate') {
            restoreTarget.current = result.at;
            onActivate(result.at);
            return;
          }
          focusPosition(result.to);
        },
      };
    },
    [grid, position, onActivate, focusPosition],
  );

  return useMemo(() => ({ itemProps, notifyDetailClosed }), [itemProps, notifyDetailClosed]);
}
